import React, { useState } from 'react';
import api, { formatApiError } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { KeyRound, User } from 'lucide-react';
import PasswordInput from '../components/PasswordInput';

export default function Settings() {
  const { user } = useAuth();

  return (
    <div className="space-y-8 max-w-lg">
      <header>
        <div className="overline">Account</div>
        <h1 className="heading text-3xl font-black text-ink">Settings</h1>
        <p className="text-sm text-zinc-500 mt-1">Manage your account details and security.</p>
      </header>

      {/* Account info */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <User size={14} className="text-zinc-400" />
          <span className="overline">Profile</span>
        </div>
        <div className="card p-5 space-y-3">
          <Row label="Name"          value={user?.name} />
          <Row label="Email"         value={user?.email} />
          <Row label="Role"          value={user?.role} />
          <Row label="Department"    value={user?.department || '—'} />
          <Row label="Employee Code" value={user?.employee_code || '—'} />
        </div>
      </section>

      {/* Change password */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <KeyRound size={14} className="text-zinc-400" />
          <span className="overline">Security</span>
        </div>
        <ChangePasswordForm />
      </section>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center gap-4 text-sm">
      <span className="overline w-32 shrink-0">{label}</span>
      <span className="text-ink font-medium">{value}</span>
    </div>
  );
}

function ChangePasswordForm() {
  const [form, setForm] = useState({ old_password: '', new_password: '', confirm_password: '' });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (form.new_password.length < 4) {
      setErr('New password must be at least 4 characters.');
      return;
    }
    if (form.new_password !== form.confirm_password) {
      setErr('New passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/change-password', {
        old_password: form.old_password,
        new_password: form.new_password,
      });
      toast.success('Password updated successfully');
      setForm({ old_password: '', new_password: '', confirm_password: '' });
    } catch (e) {
      const msg = e?.response?.data?.message;
      setErr(Array.isArray(msg) ? msg.join(', ') : (msg || formatApiError(e?.response?.data?.detail, e?.response?.data) || 'Failed to update password'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card p-5">
      <h2 className="font-bold text-ink mb-4">Change Password</h2>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="label">Current Password *</label>
          <PasswordInput
            required className="input"
            value={form.old_password} onChange={(e) => set('old_password', e.target.value)}
            autoComplete="current-password"
          />
        </div>
        <div>
          <label className="label">New Password *</label>
          <PasswordInput
            required className="input"
            value={form.new_password} onChange={(e) => set('new_password', e.target.value)}
            autoComplete="new-password"
          />
          <p className="text-xs text-zinc-400 mt-1">Minimum 4 characters.</p>
        </div>
        <div>
          <label className="label">Confirm New Password *</label>
          <PasswordInput
            required className="input"
            value={form.confirm_password} onChange={(e) => set('confirm_password', e.target.value)}
            autoComplete="new-password"
          />
        </div>
        {err && <div className="text-red-700 text-sm bg-red-50 border border-red-200 p-2 rounded-sm">{err}</div>}
        <button type="submit" className="btn-primary" disabled={loading}>
          {loading ? 'Saving…' : 'Update Password'}
        </button>
      </form>
    </div>
  );
}
